import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard,
  FileText,
  MapPin,
  Radio,
  Building2,
  User,
  Settings,
  Bell,
  Brain,
  FolderLock,
  Plus,
  Upload,
  Zap,
  AlertTriangle,
  Search,
  Clock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getAllRecents, recordRecent, type RecentItem } from "@/lib/recent-items";

const navItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Reports", url: "/reports", icon: FileText },
  { title: "Sites", url: "/sites", icon: MapPin },
  { title: "Field Data", url: "/field", icon: Radio },
  { title: "Alert History", url: "/notifications", icon: Bell },
  { title: "AI Insights", url: "/insights", icon: Brain },
  { title: "Document Vault", url: "/documents", icon: FolderLock },
  { title: "Company", url: "/company", icon: Building2 },
  { title: "Profile & Career", url: "/profile", icon: User },
  { title: "Settings", url: "/settings", icon: Settings },
];

interface DBReport {
  id: string;
  report_id: string;
  title: string;
  property_address: string | null;
}

interface DBSite {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
}

interface DBNotification {
  id: string;
  site: string;
  type: string;
  severity: string;
  created_at: string;
}

interface DBDocument {
  id: string;
  file_name: string;
  status: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [dbReports, setDbReports] = useState<DBReport[]>([]);
  const [dbSites, setDbSites] = useState<DBSite[]>([]);
  const [alerts, setAlerts] = useState<DBNotification[]>([]);
  const [documents, setDocuments] = useState<DBDocument[]>([]);
  const [recents, setRecents] = useState<RecentItem[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (open)
      setRecents(
        getAllRecents(6).filter(
          (r) => !/^\/(sites|reports)\/(SITE|RPT)-/.test(r.href),
        ),
      );
  }, [open]);

  useEffect(() => {
    if (!open || !user) return;
    supabase
      .from("reports")
      .select("id, report_id, title, property_address")
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => data && setDbReports(data));
    supabase
      .from("sites")
      .select("id, name, address, city, state")
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => data && setDbSites(data));
    supabase
      .from("notifications")
      .select("id, site, type, severity, created_at")
      .eq("dismissed", false)
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => data && setAlerts(data));
    supabase
      .from("documents")
      .select("id, file_name, status")
      .order("created_at", { ascending: false })
      .limit(20)
      .then(({ data }) => data && setDocuments(data));
  }, [open, user]);

  const go = useCallback(
    (path: string) => {
      onOpenChange(false);
      navigate(path);
    },
    [navigate, onOpenChange],
  );

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search reports, sites, alerts, documents…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {recents.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recents.map((r) => (
                <CommandItem
                  key={`${r.kind}-${r.id}`}
                  value={`recent ${r.label} ${r.sublabel ?? ""}`}
                  onSelect={() => go(r.href)}
                >
                  <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{r.label}</span>
                  {r.sublabel && (
                    <span className="text-xs text-muted-foreground capitalize">
                      {r.kind} · {r.sublabel}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Quick Actions">
          <CommandItem
            onSelect={() => {
              recordRecent({ kind: "action", id: "new-esa", label: "New Phase I ESA Report", href: "/reports/new/phase1-esa" });
              go("/reports/new/phase1-esa");
            }}
          >
            <Plus className="mr-2 h-4 w-4 text-primary" />
            New Phase I ESA Report
            <span className="ml-auto text-[10px] text-muted-foreground">⌘N</span>
          </CommandItem>
          <CommandItem
            onSelect={() => {
              recordRecent({ kind: "action", id: "upload-doc", label: "Upload Document", href: "/documents" });
              go("/documents");
            }}
          >
            <Upload className="mr-2 h-4 w-4 text-primary" />
            Upload Document
          </CommandItem>
          <CommandItem
            onSelect={() => {
              recordRecent({ kind: "action", id: "view-insights", label: "View AI Insights", href: "/insights" });
              go("/insights");
            }}
          >
            <Zap className="mr-2 h-4 w-4 text-primary" />
            View AI Insights
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Navigation">
          {navItems.map((item) => (
            <CommandItem key={item.url} onSelect={() => go(item.url)}>
              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {item.title}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {dbSites.length > 0 && (
          <CommandGroup heading="Sites">
            {dbSites.map((site) => (
              <CommandItem
                key={site.id}
                value={`${site.name} ${site.address ?? ""} ${site.city ?? ""}`}
                onSelect={() => go(`/sites/${site.id}`)}
              >
                <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
                <span className="flex-1 truncate">{site.name}</span>
                <span className="text-xs text-muted-foreground">
                  {[site.city, site.state].filter(Boolean).join(", ")}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {dbReports.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Reports">
              {dbReports.map((r) => (
                <CommandItem
                  key={r.id}
                  value={`${r.title} ${r.report_id} ${r.property_address || ""}`}
                  onSelect={() => go(`/reports/${r.id}`)}
                >
                  <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{r.title}</span>
                  <span className="text-xs text-muted-foreground">{r.report_id}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {alerts.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recent Alerts">
              {alerts.slice(0, 10).map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.site} ${a.type} ${a.severity}`}
                  onSelect={() => go("/notifications")}
                >
                  <AlertTriangle
                    className={`mr-2 h-4 w-4 ${a.severity === "critical" ? "text-destructive" : "text-warning"}`}
                  />
                  <span className="flex-1 truncate">
                    {a.type.toUpperCase()} — {a.site}
                  </span>
                  <span className="text-xs text-muted-foreground capitalize">{a.severity}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {documents.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Documents">
              {documents.map((d) => (
                <CommandItem
                  key={d.id}
                  value={d.file_name}
                  onSelect={() => go("/documents")}
                >
                  <FolderLock className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 truncate">{d.file_name}</span>
                  <span className="text-xs text-muted-foreground capitalize">{d.status}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
